// src/components/profile/AchievementGrid.tsx
// Komponen grid prestasi dengan card layout responsif

import React, { useState, useEffect } from "react";
import { Trophy, Medal, Award, Star, Calendar, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import SpotlightCard from "../SpotlightCard/SpotlightCard";
import BlurText from "../../TextAnimations/BlurText/BlurText";
import { useOptimizedProfile } from "../../hooks/useOptimizedProfile";
import AchievementModal from "./AchievementModal";
import { PhotoProvider, PhotoView } from "react-photo-view";

const AchievementGrid: React.FC = () => {
    const { profileData, loading, error } = useOptimizedProfile();
    const achievements = profileData?.achievements || [];
    const [selectedAchievement, setSelectedAchievement] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    const totalPages = Math.ceil(achievements.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentAchievements = achievements.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [achievements.length, totalPages, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        const element = document.getElementById("achievements");
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 4) {
                pages.push(1, 2, 3, 4, 5, "...", totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
            }
        }
        return pages;
    };

    // Handler untuk membuka modal
    const handleAchievementClick = (achievement: any) => {
        setSelectedAchievement(achievement);
        setIsModalOpen(true);
    };

    // Handler untuk menutup modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedAchievement(null);
    };
    // Fungsi untuk mendapatkan icon berdasarkan level prestasi
    const getAchievementIcon = (level: string, className = "h-8 w-8") => {
        switch (level) {
            case "Nasional":
                return <Trophy className={`${className} text-yellow-500`} />;
            case "Provinsi":
                return <Medal className={`${className} text-gray-400`} />;
            case "Kota":
            case "Kabupaten":
                return <Award className={`${className} text-orange-500`} />;
            case "Sekolah":
                return <Star className={`${className} text-blue-500`} />;
            case "Event Organizer":
                return <Star className={`${className} text-purple-500`} />;
            default:
                return <Trophy className={`${className} text-gray-500`} />;
        }
    };

    // Fungsi untuk mendapatkan warna border berdasarkan level
    const getBorderColor = (level: string) => {
        switch (level) {
            case "Nasional":
                return "border-yellow-200 hover:border-yellow-300";
            case "Provinsi":
                return "border-gray-200 hover:border-gray-300";
            case "Kota":
            case "Kabupaten":
                return "border-orange-200 hover:border-orange-300";
            case "Sekolah":
                return "border-blue-200 hover:border-blue-300";
            case "Event Organizer":
                return "border-purple-200 hover:border-purple-300";
            default:
                return "border-gray-200 hover:border-gray-300";
        }
    };

    // Fungsi untuk mendapatkan warna background berdasarkan level
    const getBackgroundColor = (level: string) => {
        switch (level) {
            case "Nasional":
                return "bg-yellow-50 hover:bg-yellow-100";
            case "Provinsi":
                return "bg-gray-50 hover:bg-gray-100";
            case "Kota":
            case "Kabupaten":
                return "bg-orange-50 hover:bg-orange-100";
            case "Sekolah":
                return "bg-blue-50 hover:bg-blue-100";
            case "Event Organizer":
                return "bg-purple-50 hover:bg-purple-100";
            default:
                return "bg-gray-50 hover:bg-gray-100";
        }
    };

    // Fungsi untuk mendapatkan badge color berdasarkan level
    const getBadgeColor = (level: string) => {
        switch (level) {
            case "Nasional":
                return "bg-yellow-100 text-yellow-800";
            case "Provinsi":
                return "bg-gray-100 text-gray-800";
            case "Kota":
            case "Kabupaten":
                return "bg-orange-100 text-orange-800";
            case "Sekolah":
                return "bg-blue-100 text-blue-800";
            case "Event Organizer":
                return "bg-purple-100 text-purple-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Prestasi
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse">
                            <div className="bg-gray-200 rounded-xl p-6 h-48"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Prestasi
                </h2>
                <div className="text-center py-12">
                    <Trophy className="h-16 w-16 text-red-300 mx-auto mb-4" />
                    <p className="text-red-500 text-lg">
                        Error memuat data prestasi: {error}
                    </p>
                </div>
            </div>
        );
    }

    if (!achievements || achievements.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Prestasi
                </h2>
                <div className="text-center py-12">
                    <Trophy className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">
                        Belum ada prestasi yang ditambahkan
                    </p>
                </div>
            </div>
        );
    }

    return (
        <PhotoProvider>
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 md:p-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <BlurText
                        text="Prestasi"
                        delay={30}
                        animateBy="words"
                        direction="top"
                        onAnimationComplete={() =>
                            console.log("Prestasi animation completed!")
                        }
                        className="text-xl sm:text-2xl font-bold text-gray-900"
                    />
                    <div className="flex items-center text-xs sm:text-sm text-gray-500">
                        <Trophy className="h-4 w-4 mr-1 text-yellow-500" />
                        <span>{achievements.length} prestasi</span>
                    </div>
                </div>

                {/* Achievement Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-6">
                    {currentAchievements.map((achievement) => (
                        <div
                            key={achievement.id}
                            onClick={() => handleAchievementClick(achievement)}
                            className="cursor-pointer group flex"
                        >
                            <SpotlightCard
                                className={`
                      relative group transition-all duration-300 w-full flex flex-col justify-between
                      border-2 rounded-2xl p-3.5 sm:p-4 shadow-sm hover:shadow-lg
                      ${getBackgroundColor(achievement.level)} ${getBorderColor(
                                    achievement.level
                                )}
                    `}
                                spotlightColor="rgba(150, 150, 150, 0.3)"
                            >
                                <div>
                                    {/* Achievement Photo (if available) */}
                                    {achievement.image_url ? (
                                        <div
                                            className="relative w-full h-44 sm:h-48 mb-3.5 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 cursor-pointer shadow-xs group/img"
                                            onClick={(e) => {
                                                // Buka PhotoView tanpa membuka modal
                                                e.stopPropagation();
                                            }}
                                        >
                                            <PhotoView src={achievement.image_url}>
                                                <img
                                                    src={achievement.image_url}
                                                    alt={achievement.title}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-105"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                    }}
                                                />
                                            </PhotoView>

                                            {/* Level Icon Badge on top of image */}
                                            <div className="absolute top-2.5 right-2.5 z-10 p-1.5 bg-white/95 backdrop-blur-xs rounded-full shadow-md pointer-events-none">
                                                {getAchievementIcon(achievement.level, "h-4 w-4 sm:h-5 sm:w-5")}
                                            </div>

                                            {/* Gradient Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

                                            {/* View Photo Hint Pill */}
                                            <div className="absolute bottom-2 right-2 z-10 px-2 py-1 bg-black/60 backdrop-blur-xs rounded-lg text-[11px] text-white flex items-center gap-1 opacity-90 sm:opacity-0 sm:group-hover/img:opacity-100 transition-opacity pointer-events-none">
                                                <Eye className="h-3 w-3" />
                                                <span>Lihat Foto</span>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Fallback icon when no photo */
                                        <div className="flex justify-center mb-3 sm:mb-4 pt-2">
                                            <div className="p-3 bg-white rounded-full shadow-sm group-hover:shadow-md transition-shadow">
                                                {getAchievementIcon(achievement.level, "h-8 w-8")}
                                            </div>
                                        </div>
                                    )}

                                    {/* Achievement Content */}
                                    <div className="text-center px-1">
                                        <BlurText
                                            text={achievement.title}
                                            delay={30}
                                            animateBy="words"
                                            direction="top"
                                            onAnimationComplete={() => {}}
                                            className="font-bold text-gray-900 mb-2 text-base sm:text-lg leading-snug break-words"
                                            style={{
                                                fontFamily: "ABeeZee, sans-serif",
                                            }}
                                        />

                                        {/* Level and Year Badge */}
                                        <div className="flex flex-col items-center gap-2 mb-2 sm:mb-3">
                                            <span
                                                className={`
                          inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-semibold gap-1 text-center max-w-full break-words
                          ${getBadgeColor(achievement.level)}
                        `}
                                            >
                                                <Calendar className="h-3 w-3 flex-shrink-0" />
                                                <span>
                                                    {achievement.level} -{" "}
                                                    {new Date(
                                                        achievement.achievement_date
                                                    ).toLocaleDateString("id-ID", {
                                                        year: "numeric",
                                                        month: "short",
                                                        day: "numeric",
                                                    })}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Card Footer: Detail Link */}
                                <div className="mt-3 pt-2.5 border-t border-gray-200/60 flex items-center justify-center text-xs font-semibold text-blue-600 group-hover:text-blue-700 transition-colors">
                                    <span>Lihat Detail Prestasi</span>
                                    <ChevronRight className="h-3.5 w-3.5 ml-0.5 transform group-hover:translate-x-0.5 transition-transform" />
                                </div>
                            </SpotlightCard>
                        </div>
                    ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 border-t border-gray-100 pt-4 sm:pt-6">
                        <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                            Menampilkan <span className="font-semibold text-gray-700">{startIndex + 1}</span> -{" "}
                            <span className="font-semibold text-gray-700">{Math.min(startIndex + itemsPerPage, achievements.length)}</span> dari{" "}
                            <span className="font-semibold text-gray-700">{achievements.length}</span> prestasi
                        </div>

                        {/* Mobile Native Pagination (sm:hidden) */}
                        <div className="flex sm:hidden items-center justify-between w-full gap-2 pt-1">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`flex-1 py-2.5 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                                    currentPage === 1
                                        ? "border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed"
                                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-100 active:scale-95 cursor-pointer shadow-xs"
                                }`}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                <span>Sebelumnya</span>
                            </button>

                            <span className="px-3 py-2 text-xs font-bold text-gray-700 bg-gray-100 rounded-xl whitespace-nowrap">
                                {currentPage} / {totalPages}
                            </span>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`flex-1 py-2.5 px-3 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                                    currentPage === totalPages
                                        ? "border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed"
                                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-100 active:scale-95 cursor-pointer shadow-xs"
                                }`}
                            >
                                <span>Berikutnya</span>
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Desktop Numbered Pagination (hidden sm:flex) */}
                        <div className="hidden sm:flex items-center gap-1 sm:gap-2">
                            {/* Previous Button */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                                    currentPage === 1
                                        ? "border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50"
                                        : "border-gray-300 text-gray-700 hover:bg-gray-100 active:scale-95 cursor-pointer"
                                }`}
                                aria-label="Halaman Sebelumnya"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>

                            {/* Page Numbers */}
                            {getPageNumbers().map((page, idx) => {
                                if (page === "...") {
                                    return (
                                        <span
                                            key={`ellipsis-${idx}`}
                                            className="px-2 py-1 text-gray-400 text-sm select-none"
                                        >
                                            ...
                                        </span>
                                    );
                                }
                                const isCurrent = currentPage === page;
                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(Number(page))}
                                        className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                                            isCurrent
                                                ? "bg-blue-600 text-white shadow-sm"
                                                : "border border-gray-200 text-gray-700 hover:bg-gray-100 active:scale-95"
                                        }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                            {/* Next Button */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                                    currentPage === totalPages
                                        ? "border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50"
                                        : "border-gray-300 text-gray-700 hover:bg-gray-100 active:scale-95 cursor-pointer"
                                }`}
                                aria-label="Halaman Selanjutnya"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Summary Stats */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                    {/* Level Statistics */}
                    <div className="mb-2 sm:mb-6">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                            Berdasarkan Level
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
                            {/* Nasional */}
                            <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-yellow-50 transition-colors duration-200 border sm:border-0 border-gray-100">
                                <div className="text-xl sm:text-2xl font-bold text-yellow-600">
                                    {
                                        achievements.filter(
                                            (a) => a.level === "Nasional"
                                        ).length
                                    }
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">
                                    Nasional
                                </div>
                            </div>

                            {/* Provinsi */}
                            <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-gray-100 transition-colors duration-200 border sm:border-0 border-gray-100">
                                <div className="text-xl sm:text-2xl font-bold text-gray-600">
                                    {
                                        achievements.filter(
                                            (a) => a.level === "Provinsi"
                                        ).length
                                    }
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">
                                    Provinsi
                                </div>
                            </div>

                            {/* Kota/Kabupaten */}
                            <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-orange-50 transition-colors duration-200 border sm:border-0 border-gray-100">
                                <div className="text-xl sm:text-2xl font-bold text-orange-600">
                                    {
                                        achievements.filter((a) =>
                                            ["Kota", "Kabupaten"].includes(
                                                a.level
                                            )
                                        ).length
                                    }
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">
                                    Kota/Kab
                                </div>
                            </div>

                            {/* Sekolah */}
                            <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-blue-50 transition-colors duration-200 border sm:border-0 border-gray-100">
                                <div className="text-xl sm:text-2xl font-bold text-blue-600">
                                    {
                                        achievements.filter(
                                            (a) => a.level === "Sekolah"
                                        ).length
                                    }
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">
                                    Sekolah
                                </div>
                            </div>

                            {/* Event Organizer */}
                            <div className="text-center p-2.5 sm:p-3 rounded-xl bg-gray-50 sm:bg-transparent hover:bg-purple-50 transition-colors duration-200 border sm:border-0 border-gray-100 col-span-2 sm:col-span-1">
                                <div className="text-xl sm:text-2xl font-bold text-purple-600">
                                    {
                                        achievements.filter(
                                            (a) => a.level === "Event Organizer"
                                        ).length
                                    }
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600">
                                    Event Organizer
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Achievement Modal */}
                <AchievementModal
                    achievement={selectedAchievement}
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                />
            </div>
        </PhotoProvider>
    );
};

export default AchievementGrid;
